// Helpers for the lottery-style number generators (Lottery / Powerball / Mega
// Millions). These presets emit a raw JSON object describing the generated
// number sets; we parse it for the <LotteryBalls /> visual renderer and format
// it back to readable text for copy/export so raw JSON never leaks to the user.

export type LotterySet = {
  main: number[];
  // Bonus/special balls. Powerball & Mega Millions emit a single number;
  // the generic lottery generator can emit several (e.g. EuroMillions' two
  // Lucky Stars), so this is always normalized to an array.
  specials?: number[];
  analysis?: string;
};

export const LOTTERY_PRESETS = [
  'lottery-number-generator',
  'powerball-number-generator',
  'mega-millions-number-generator',
] as const;

export function isLotteryPreset(slug?: string | null): boolean {
  return !!slug && (LOTTERY_PRESETS as readonly string[]).includes(slug);
}

export function lotterySpecialLabel(slug?: string | null): string | undefined {
  if (slug === 'powerball-number-generator') return 'Powerball';
  if (slug === 'mega-millions-number-generator') return 'Mega Ball';
  return undefined;
}

type RawLotterySet = { main?: unknown; special?: unknown; analysis?: unknown };

function toSpecials(raw: unknown): number[] | undefined {
  const values = Array.isArray(raw) ? raw : [raw];
  const specials = values
    .map((n) => Number(n))
    .filter((n) => Number.isFinite(n));
  return specials.length > 0 ? specials : undefined;
}

function normalizeLotterySets(raw: RawLotterySet[]): LotterySet[] {
  return raw
    .map((e) => ({
      main: Array.isArray(e?.main)
        ? e.main.map((n) => Number(n)).filter((n) => Number.isFinite(n))
        : [],
      specials: toSpecials(e?.special),
      analysis:
        typeof e?.analysis === 'string' && e.analysis.trim()
          ? e.analysis.trim()
          : undefined,
    }))
    .filter((s) => s.main.length > 0);
}

/**
 * Parse the model's JSON output into structured sets. Tolerant of code fences,
 * surrounding prose, and malformed JSON (LLMs occasionally emit a stray brace):
 * tries a strict JSON parse first, then falls back to extracting each set's
 * fields by hand. Returns null if no sets are found (caller falls back to text).
 */
export function parseLotterySets(text?: string | null): LotterySet[] | null {
  if (!text || typeof text !== 'string') return null;

  // 1) Strict JSON parse of the first {...} block.
  const start = text.indexOf('{');
  const end = text.lastIndexOf('}');
  if (start !== -1 && end > start) {
    try {
      const parsed = JSON.parse(text.slice(start, end + 1)) as {
        sets?: unknown;
      };
      if (Array.isArray(parsed?.sets)) {
        const sets = normalizeLotterySets(parsed.sets as RawLotterySet[]);
        if (sets.length > 0) return sets;
      }
    } catch {
      // Fall through to tolerant extraction below.
    }
  }

  // 2) Tolerant extraction — survives malformed JSON by pulling each set's
  // fields out in order with regex.
  const mains = Array.from(text.matchAll(/"main"\s*:\s*\[([^\]]*)\]/g)).map(
    (m) =>
      m[1]
        .split(',')
        .map((n) => Number(n.trim()))
        .filter((n) => Number.isFinite(n)),
  );
  if (mains.length === 0) return null;

  // "special" may be a bare number (Powerball/Mega Millions) or an array (the
  // generic generator, e.g. EuroMillions' two Lucky Stars).
  const specials = Array.from(
    text.matchAll(/"special"\s*:\s*(\[[^\]]*\]|-?\d+(?:\.\d+)?)/g),
  ).map((m) =>
    toSpecials(
      m[1].startsWith('[')
        ? m[1]
            .slice(1, -1)
            .split(',')
            .map((n) => n.trim())
            .filter(Boolean)
        : m[1],
    ),
  );
  const analyses = Array.from(
    text.matchAll(/"analysis"\s*:\s*"((?:[^"\\]|\\.)*)"/g),
  ).map((m) => m[1].replace(/\\"/g, '"').replace(/\\n/g, ' ').trim());

  const sets = mains
    .map((main, i) => ({
      main,
      specials: specials[i],
      analysis: analyses[i] || undefined,
    }))
    .filter((s) => s.main.length > 0);

  return sets.length > 0 ? sets : null;
}

/** Render sets as readable text for copy/export (never raw JSON). */
export function formatLotterySetsAsText(
  sets: LotterySet[],
  specialLabel?: string,
): string {
  return sets
    .map((set, i) => {
      let line = `Set ${i + 1}: ${set.main.join(', ')}`;
      if (set.specials?.length) {
        line += ` — ${specialLabel || 'Bonus'}: ${set.specials.join(', ')}`;
      }
      if (set.analysis) {
        line += `\nAnalysis: ${set.analysis}`;
      }
      return line;
    })
    .join('\n\n');
}
